/**
 * HA.ts — helpers globaux (ESM, ha/440).
 *
 * Conversion namespace→ESM du fichier racine. Recette appliquée :
 *  1. Suppression du wrapper `namespace HA { ... }` (les exports deviennent
 *     des exports ESM top-level).
 *  2. Suppression des `import X = HA.Core.X` (legacy alias namespace) ; les
 *     types out-of-cluster référencés (HANotification, Data) sont typés via
 *     interfaces minimales LOCALES — ha/441 codemod remplacera par les vrais
 *     imports ESM une fois ces modules portés.
 *  3. La fonction `createService(ns, name, data) = new (ns)[name](data)` est
 *     CONSERVÉE pour rétrocompat (legacy l'appelle encore) mais le cluster
 *     converti ne l'utilise plus : il passe par `ControllerRegistry.create`.
 *     ha/441 supprimera tous les call-sites legacy puis cette fonction.
 *
 * Note IMG_OBSERVER : initialisé top-level. En ESM le module est singleton,
 * donc l'observer est initialisé une seule fois à la première import — même
 * sémantique que le legacy avec `let IMG_OBSERVER = new IntersectionObserver(...)`.
 */

// ─── Contrats de service (ha/445) ────────────────────────────────────────────
// Importés via `import type` → effacés à la compilation, aucun cycle d'import.
// Les implémentations runtime restent enregistrées via setters/slots au boot.

import type { IAppService, IWebPageService, IDataRepository } from "./ha/Core/Service/contracts";

/** Stub local de `HA.Core.Notification.HANotification`. ha/441 : import réel. */
interface HANotificationStub {
    log(msg: unknown): void;
    isDebug(): boolean;
    watch(data: unknown): void;
}

// ─── État global (singletons module-scoped) ──────────────────────────────────

export let APPSERVICE: IAppService | undefined;
export let SERVICE4APP: any;
export let DATAREPO: IDataRepository | undefined;
export let NOTIFICATION: HANotificationStub | undefined;
export let HAAPPID: string | undefined;

export const IMG_OBSERVER: IntersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            const image = entry.target as HTMLImageElement;
            if (image.dataset.src) {
                image.src = image.dataset.src;
            }
            IMG_OBSERVER.unobserve(image);
        }
    });
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Slot DataRepository (recette §3.1) — évite le cycle HA ↔ Repository/Data.
// Repository/Data.ts appelle registerDataRepository(Data) à l'import ; le boot
// (main.ts) side-effect-importe ce module → le slot est rempli avant le 1er
// getDataRepository(). Fallback {} si non enregistré (tests bootstrap-only).
let _DataRepoCtor: (new () => IDataRepository) | undefined;

export function registerDataRepository(ctor: new () => IDataRepository): void {
    _DataRepoCtor = ctor;
}

export function getDataRepository(): IDataRepository {
    if (!DATAREPO) {
        DATAREPO = _DataRepoCtor ? new _DataRepoCtor() : ({} as IDataRepository);
    }
    return DATAREPO;
}

export function getService(id: string): unknown {
    return getAppService().getService(id);
}

export function getWebPageService(): IWebPageService {
    return getAppService().getWebPageService();
}

export function setAppService(appService: IAppService): void {
    APPSERVICE = appService;
}

export function getAppService(): IAppService {
    return APPSERVICE as IAppService;
}

export function setService4App(appService: any): void {
    SERVICE4APP = appService;
}

export function getService4App(): any {
    return SERVICE4APP;
}

export function timeout(func: () => void, time: number): ReturnType<typeof setTimeout> {
    return setTimeout(func, time);
}

export function setNotification(notification: HANotificationStub): void {
    NOTIFICATION = notification;
}

export function log(msg: unknown): void {
    if (NOTIFICATION) {
        NOTIFICATION.log(msg);
    } else {
        log2([isDebug(), msg]);
    }
}

export function log2(msg: unknown): void {
    // eslint-disable-next-line no-console
    console.log(msg);
}

export function setAppId(id: string): void {
    HAAPPID = id;
}

export function getAppId(): string | undefined {
    return HAAPPID;
}

export function lazyloadImage(img: HTMLImageElement): void {
    IMG_OBSERVER.observe(img);
}

export function notifyUser(msg: unknown): void {
    log(msg);
}

export function isDebug(): boolean {
    return NOTIFICATION ? NOTIFICATION.isDebug() : false;
}

/**
 * Legacy : `new (namespace)[serviceName](data)`. CONSERVÉ pour rétrocompat AMD,
 * le cluster converti ne l'appelle plus (il passe par `ControllerRegistry`).
 * ha/441 : supprimer une fois les call-sites legacy migrés.
 */
export function createService(namespace: any, serviceName: string, data: any): unknown {
    return new (namespace as Record<string, new (d: any) => unknown>)[serviceName](data);
}

export function ptime(...data: unknown[]): void {
    log2("time:");
    const date = new Date();
    log2(date.getMilliseconds() + " - " + date.toUTCString());
    if (data) {
        log2(data);
    }
}

export function toObj(data: Map<unknown, unknown>): Record<string, unknown> {
    const obj: Record<string, unknown> = Object.create(null);
    if (data instanceof Map) {
        data.forEach((v, k) => {
            obj[String(k)] = v;
        });
    }
    return obj;
}

export function toMap(
    data: Map<unknown, unknown> | Record<string, unknown> | null | undefined,
    map?: Map<unknown, unknown>,
): Map<unknown, unknown> {
    map = map || new Map();
    if (data instanceof Map) {
        data.forEach((v, k) => {
            map!.set(k, v);
        });
    } else if (data) {
        Object.keys(data).forEach((key) => {
            map!.set(key, (data as Record<string, unknown>)[key]);
        });
    }
    return map;
}
